/* eslint-disable @typescript-eslint/no-explicit-any */
import { Question } from '../Beneficiary/BeneficiaryLanding';
import JSON5 from 'json5';

export interface StoryValue {
  fileId?: string;
  fileName?: string;
  title?: string;
  text?: string;
  url?: string;
}

export interface LessonValue {
  text: string;
  lesson: string | string[];
}

type Value =
  | {
      text?: StoryValue;
      answerId: number;
      type: 'upload_photo';
    }
  | {
      text?: LessonValue;
      answerId: number;
      type: 'categories_lesson';
    };

interface AnswerValue {
  text: string | StoryValue | LessonValue;
}

export interface NarrativeSubmitReference {
  isValid: () => boolean;
  handleSubmit: () => StoryValue | LessonValue;
}

export interface NarrativeProps {
  hideBottomBar?: boolean;
  question: Question;
  programName: string;
  value?: Value | string;
  reportId: number | string;
  isThirdParty: boolean;
  handleBack: () => unknown;
  onNextSection: () => unknown;
  onPreviousSection: () => unknown;
  handleNext: (buttonPressed: boolean) => unknown;
  handleValueChange: (questionId: number, answer: AnswerValue) => unknown;
}

export interface NarrativeStoryProps {
  storyFileId?: string;
  storyFileName?: string;
  storyFileUrl?: string;
  storyTitle?: string;
  storyDescription?: string;
  hideBottomBar?: NarrativeProps['hideBottomBar'];
}

export interface NarrativeLessonProps {
  storyLesson: string[];
  lessonDescription: string;
  hideBottomBar?: NarrativeProps['hideBottomBar'];
}

export const BENEFICIARY_QUESTION_ID = 60001;
export const NARRATIVE_STORY_QUESTION_ID = 70001;
export const NARRATIVE_LESSON_QUESTION_ID = 70002;

export const parseStoryValue = (
  value: NarrativeProps['value'],
): NarrativeStoryProps => {
  const defaultValue: NarrativeStoryProps = {};
  if (!value) return defaultValue;
  if (typeof value === 'string') {
    try {
      const values:
        | StoryValue
        | { text: StoryValue; title: undefined } = JSON5.parse(value);
      if (values.title) {
        return {
          storyTitle: values.title,
          storyDescription: values.text,
          storyFileId: values.fileId,
          storyFileName: values.fileName,
          storyFileUrl: values.url,
        };
      } else if (values.text && typeof values.text !== 'string') {
        return {
          storyTitle: values.text.title,
          storyDescription: values.text.text,
          storyFileId: values.text.fileId,
          storyFileName: values.text.fileName,
          storyFileUrl: values.text.url,
        };
      }
      return defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }

  if (!value.text || value.type === 'categories_lesson') return defaultValue;

  // there is a bug somewhere that is adding `text: {...}` to the values.
  if (typeof value.text.text !== 'string') {
    return {
      storyFileId: (value.text as any).text.fileId,
      storyTitle: (value.text as any).text.title,
      storyDescription: (value.text as any).text.text,
      storyFileName: (value.text as any).text.fileName,
      storyFileUrl: (value.text as any).text.url,
    };
  }
  return {
    storyFileId: value.text.fileId,
    storyTitle: value.text.title,
    storyDescription: value.text.text,
    storyFileName: value.text.fileName,
    storyFileUrl: value.text.url,
  };
};

const defaultLessonValue: NarrativeLessonProps = {
  storyLesson: [],
  lessonDescription: '',
};
export const parseLessonValue = (
  value: NarrativeProps['value'],
): NarrativeLessonProps => {
  if (!value) return defaultLessonValue;
  if (typeof value === 'string') {
    try {
      const values:
        | LessonValue
        | { text: LessonValue; lesson: undefined } = JSON5.parse(value);
      if (values.lesson) {
        return {
          storyLesson:
            typeof values.lesson === 'string' ? [values.lesson] : values.lesson,
          lessonDescription: values.text,
        };
      } else if (values.text && typeof values.text !== 'string') {
        return {
          storyLesson:
            typeof values.text.lesson === 'string'
              ? [values.text.lesson]
              : values.text.lesson,
          lessonDescription: values.text.text,
        };
      }
      return defaultLessonValue;
    } catch (e) {
      return defaultLessonValue;
    }
  }
  if (!value.text || value.type === 'upload_photo') return defaultLessonValue;
  return {
    lessonDescription: value.text.text,
    storyLesson:
      typeof value.text.lesson === 'string'
        ? [value.text.lesson]
        : value.text.lesson,
  };
};
